import { Button, Row, Text } from 'components';
import { Api, theme } from 'lib';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  diffRewards,
  durationToMinutes,
  FANWAVE,
  fmtCount,
  minutesToHoursLabel,
  POST_FT_BOUNDS,
  splitDuration,
  validatePostFtMinutes,
  validateTapTarget
} from '../constants';
import RewardsFields, {
  normalizeRewards,
  rewardsWarnings,
  toEditableRewards,
  validateRewards
} from './RewardsFields';
import { Badge, DurationField, Section } from './ui';

/**
 * Global default FanWave config (P4P Admin only) — tap target, free-form post-FT window and
 * the platform-wide reward rules (mockup sections 3–8). Applies to every team unless a
 * team-specific override exists. Backend: GET/PUT /admin/fanwave/globalConfig|setGlobalConfig.
 * Reward fields merge server-side, so the save only sends what actually changed.
 */
const GlobalDefaultsSection = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isDefault, setIsDefault] = useState(true);

  const [globalTarget, setGlobalTarget] = useState(String(FANWAVE.DEFAULT_TAP_TARGET));
  // The post-FT window is held as the admin ENTERED it (a number + a unit) and converted to
  // minutes for validation and for the request; the API only ever speaks minutes.
  const [windowValue, setWindowValue] = useState(
    () => splitDuration(FANWAVE.DEFAULT_POST_FT_MINUTES).value
  );
  const [windowUnit, setWindowUnit] = useState(
    () => splitDuration(FANWAVE.DEFAULT_POST_FT_MINUTES).unit
  );
  const [rewards, setRewards] = useState(toEditableRewards(null));
  // What the server currently has — save sends only the fields that differ from this.
  const [baseline, setBaseline] = useState(null);

  const seed = (data) => {
    setGlobalTarget(String(data.globalTarget ?? FANWAVE.DEFAULT_TAP_TARGET));
    const storedWindow = splitDuration(data.postFtMinutes ?? FANWAVE.DEFAULT_POST_FT_MINUTES);
    setWindowValue(storedWindow.value);
    setWindowUnit(storedWindow.unit);
    setRewards(toEditableRewards(data.rewards));
    setBaseline({
      globalTarget: data.globalTarget ?? FANWAVE.DEFAULT_TAP_TARGET,
      postFtMinutes: data.postFtMinutes ?? FANWAVE.DEFAULT_POST_FT_MINUTES,
      rewards: data.rewards || {}
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await Api.get('/admin/fanwave/globalConfig', null, { silent: true });
      if (!cancelled && res?.data) {
        seed(res.data);
        setIsDefault(!!res.data.isDefault);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const targetError = validateTapTarget(globalTarget);
  const postFtMinutes = durationToMinutes(windowValue, windowUnit);
  const windowError = validatePostFtMinutes(postFtMinutes);
  // Only worth spelling out the minutes when the admin did NOT type minutes — otherwise the
  // hint would just repeat the input back at them.
  const windowHint =
    !windowError && windowUnit !== 'minutes' ? `= ${fmtCount(postFtMinutes)} minutes. ` : '';

  const handleSave = async () => {
    if (targetError || windowError) return;
    const rewardsError = validateRewards(rewards, globalTarget);
    if (rewardsError) {
      setSaveError(rewardsError);
      return;
    }
    setSaveError(null);

    // Merge semantics: only send what changed so untouched fields are never rewritten.
    const body = {};
    if (Number(globalTarget) !== baseline?.globalTarget) body.globalTarget = Number(globalTarget);
    if (postFtMinutes !== baseline?.postFtMinutes) {
      body.postFtMinutes = postFtMinutes;
    }
    const rewardsPatch = diffRewards(baseline?.rewards, normalizeRewards(rewards));
    if (rewardsPatch) body.rewards = rewardsPatch;

    if (!Object.keys(body).length) {
      dispatch({ type: 'HANDLE_RES', payload: { res: 'No changes to save.' } });
      return;
    }

    setSaving(true);
    const res = await Api.put('/admin/fanwave/setGlobalConfig', body, null, { silent: true });
    setSaving(false);
    if (res?.err) {
      setSaveError(res.err);
      return;
    }
    if (res?.data) {
      seed(res.data);
      setIsDefault(false);
      dispatch({ type: 'HANDLE_RES', payload: { res: 'Global FanWave config saved.' } });
    }
  };

  return (
    <Section
      title="Global Defaults"
      description="The platform-wide FanWave settings — tap target, post-Full-Time window and reward rules. Used for every channel unless a channel or wave override wins."
      right={
        <Badge variant={isDefault ? 'muted' : 'active'} dot>
          {isDefault ? 'Built-in defaults' : 'Custom'}
        </Badge>
      }
    >
      {loading ? (
        <Text col={theme.GREY_60}>Loading…</Text>
      ) : (
        <>
          <Row start gap="24px" marg="0 0 16px" style={{ alignItems: 'flex-start' }}>
            <DurationField
              label="Post-Full-Time window"
              description={`${windowHint}How long FanWave stays active after Full-Time (${
                POST_FT_BOUNDS.MIN
              } minute – ${minutesToHoursLabel(POST_FT_BOUNDS.MAX)}).`}
              value={windowValue}
              unit={windowUnit}
              onChange={setWindowValue}
              onUnitChange={setWindowUnit}
              error={windowError}
            />
          </Row>

          {/* Sections 3–8 (incl. the per-fan tap limit) + the global default target, which
              renders inside Community Goal. */}
          <RewardsFields
            rewards={rewards}
            onChange={setRewards}
            target={globalTarget}
            effectiveTarget={globalTarget}
            onTargetChange={setGlobalTarget}
            targetError={targetError}
            targetDescription={`Default tap target for all channels. Min ${fmtCount(
              FANWAVE.MIN_TAP_TARGET
            )}, max ${fmtCount(FANWAVE.MAX_TAP_TARGET)}, in steps of ${fmtCount(
              FANWAVE.TAP_TARGET_INCREMENT
            )}.`}
            numbered={false}
          />

          {/* Accepted by the API, but not what an admin is likely to mean — e.g. progressive
              tiers sitting above the platform-wide tap limit, which no fan could reach. */}
          {rewardsWarnings(rewards, globalTarget).map((warning, i) => (
            <Text key={i} c1 col="#B45309" marg="12px 0 0" style={{ fontSize: '12.5px' }}>
              {warning}
            </Text>
          ))}

          {isDefault && (
            <Text c1 col={theme.GREY_60} marg="12px 0 0">
              No global config saved yet — these are the built-in defaults.
            </Text>
          )}

          {saveError && (
            <Text c1 col={theme.SECONDARY} marg="12px 0 0" style={{ fontSize: '12.5px' }}>
              {saveError}
            </Text>
          )}

          <Row start marg="14px 0 0">
            <Button
              text={saving ? 'Saving…' : 'Save defaults'}
              onClick={handleSave}
              bg={theme.SECONDARY}
              loading={saving}
              disabled={!!targetError || !!windowError || saving}
              small
              wid="150px"
            />
          </Row>
        </>
      )}
    </Section>
  );
};

export default GlobalDefaultsSection;
